import { useRef, useState } from "react";

export function createIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Holds one idempotency key per create-intent (text + per attached file).
 * Extra submits reuse the same keys until success or the draft changes.
 */
export function useIdempotentChatSubmit({
  intentFingerprint,
  images = [],
  serverUrl,
  onUploadError,
}) {
  const textKeyRef = useRef(createIdempotencyKey());
  const fingerprintRef = useRef(intentFingerprint);
  const imageKeysRef = useRef(new WeakMap());
  const uploadedPathsRef = useRef(new Map());
  const fileUploadPromisesRef = useRef(new Map());
  const inFlightRef = useRef(0);
  const [isSending, setIsSending] = useState(false);

  if (intentFingerprint !== fingerprintRef.current) {
    fingerprintRef.current = intentFingerprint;
    textKeyRef.current = createIdempotencyKey();
  }

  const getImageKey = (file) => {
    let key = imageKeysRef.current.get(file);
    if (!key) {
      key = createIdempotencyKey();
      imageKeysRef.current.set(file, key);
    }
    return key;
  };

  const ensureUploaded = async () => {
    const files = images || [];
    if (!files.length) return [];

    try {
      return await Promise.all(
        files.map(async (file) => {
          const key = getImageKey(file);
          if (uploadedPathsRef.current.has(key)) {
            return { key, path: uploadedPathsRef.current.get(key) };
          }
          if (!fileUploadPromisesRef.current.has(key)) {
            fileUploadPromisesRef.current.set(
              key,
              (async () => {
                const formData = new FormData();
                formData.append("image", file);
                const res = await fetch(`${serverUrl}/image/upload`, {
                  method: "POST",
                  body: formData,
                });
                const json = await res.json();
                if (!res.ok || !json?.path) {
                  throw new Error(json?.message || "Upload failed");
                }
                uploadedPathsRef.current.set(key, json.path);
                return json.path;
              })()
            );
          }
          try {
            const path = await fileUploadPromisesRef.current.get(key);
            return { key, path };
          } catch (err) {
            fileUploadPromisesRef.current.delete(key);
            throw err;
          }
        })
      );
    } catch (err) {
      console.error(err);
      if (onUploadError) onUploadError(err);
      return [];
    }
  };

  const trackSend = async (fn) => {
    inFlightRef.current += 1;
    setIsSending(true);
    try {
      return await fn();
    } finally {
      inFlightRef.current = Math.max(0, inFlightRef.current - 1);
      setIsSending(inFlightRef.current > 0);
    }
  };

  const resetAfterSuccess = () => {
    textKeyRef.current = createIdempotencyKey();
    fingerprintRef.current = "";
    uploadedPathsRef.current = new Map();
    fileUploadPromisesRef.current = new Map();
  };

  return {
    textKey: () => textKeyRef.current,
    getImageKey,
    ensureUploaded,
    trackSend,
    isSending,
    resetAfterSuccess,
  };
}

export function fingerprintProperties(properties) {
  return JSON.stringify(properties ?? []);
}

export function mutationInput({ id, properties, idempotencyKey, shouldDelete }) {
  const input = { properties };
  if (shouldDelete) {
    if (id != null) input.id = id;
    input.shouldDelete = true;
    return input;
  }
  if (id != null && id !== "") {
    input.id = id;
    return input;
  }
  if (idempotencyKey) {
    input.idempotency_key = idempotencyKey;
  }
  return input;
}

function useInFlightFlag() {
  const inFlightRef = useRef(0);
  const [isSaving, setIsSaving] = useState(false);

  const trackSave = async (fn) => {
    inFlightRef.current += 1;
    setIsSaving(true);
    try {
      return await fn();
    } finally {
      inFlightRef.current = Math.max(0, inFlightRef.current - 1);
      setIsSaving(inFlightRef.current > 0);
    }
  };

  return { isSaving, trackSave };
}

/**
 * One key per create-intent. Extra submits reuse it; editing the fingerprint
 * or calling resetAfterSuccess mints a new key.
 */
export function useIdempotentCreate({ fingerprint, enabled = true }) {
  const keyRef = useRef(createIdempotencyKey());
  const fingerprintRef = useRef(fingerprint);
  const { isSaving, trackSave } = useInFlightFlag();

  if (enabled && fingerprint !== fingerprintRef.current) {
    fingerprintRef.current = fingerprint;
    keyRef.current = createIdempotencyKey();
  }

  const resetAfterSuccess = () => {
    keyRef.current = createIdempotencyKey();
    fingerprintRef.current = "";
  };

  return {
    idempotencyKey: enabled ? keyRef.current : undefined,
    isSaving,
    trackSave,
    resetAfterSuccess,
  };
}

/**
 * Multiple create slots (parent row + junctions / follow-on rows) that must
 * stay stable across retries of the same save.
 */
export function useCreateIdempotencyKeys() {
  const keysRef = useRef(new Map());
  const fingerprintsRef = useRef(new Map());
  const { isSaving, trackSave } = useInFlightFlag();

  const keyFor = (slot, fingerprint) => {
    const prev = fingerprintsRef.current.get(slot);
    if (prev !== fingerprint || !keysRef.current.has(slot)) {
      fingerprintsRef.current.set(slot, fingerprint);
      keysRef.current.set(slot, createIdempotencyKey());
    }
    return keysRef.current.get(slot);
  };

  const resetAfterSuccess = (slot) => {
    if (slot) {
      keysRef.current.delete(slot);
      fingerprintsRef.current.delete(slot);
      return;
    }
    keysRef.current = new Map();
    fingerprintsRef.current = new Map();
  };

  return { keyFor, isSaving, trackSave, resetAfterSuccess };
}
